/* eslint-disable warp-drive/no-legacy-request-patterns */
import { warn } from '@ember/debug';
import type Store from '@linc-technologies/linced-common/services/store';
import chunk from '@linc-technologies/linced-common/utils/chunk';

// The people query sends the ids in the URL. 50 ids keep the URL under the
// proxy limit of about 8KB.
const PEOPLE_CHUNK = 50;

// loadPeople loads the people that are not in the store. It ignores empty and
// duplicate ids. Set includeFormer to also load former learners, for example
// siblings. Set type to load people of a type that the backend does not give
// without it, for example DOCTORS_AND_SPECIALISTS.
export async function loadPeople(
  store: Pick<Store, 'peekRecord' | 'query'>,
  ids: Iterable<string>,
  {
    includeFormer = false,
    type,
  }: { includeFormer?: boolean; type?: number } = {},
): Promise<void> {
  const missing = new Set<string>();
  for (const id of ids) {
    if (id && !store.peekRecord('person', id)) {
      missing.add(id);
    }
  }
  if (missing.size === 0) {
    return;
  }
  const filter = {
    ...(includeFormer && { includeFormer }),
    ...(type !== undefined && { type }),
  };
  await Promise.all(
    chunk([...missing], PEOPLE_CHUNK).map((people) =>
      store.query('person', { ...filter, people: JSON.stringify(people) }),
    ),
  );
}

// peekPerson gives the person from the store, or null. It does not load the
// person. In development, a person that is not in the store gives a warning
// with loadHint, which tells the developer where to load the person.
export function peekPerson<T>(
  store: Pick<Store, 'peekRecord'>,
  id: string,
  loadHint: string,
): T | null {
  const person = store.peekRecord('person', id) as T | null;
  warn(`The person ${id} is not in the store. ${loadHint}`, person !== null, {
    id: 'linced.person-not-loaded',
  });
  return person;
}
